import {State} from "@/types/Workflow";
import {nextColor} from "@/utils/colors";

/** Reservadas pelo papel: o começo em vermelho, o fim em verde. */
const initialColor = 'red'
const finalColor = 'green'

/**
 * As cores que a situação SEM cor escolhida recebe, nesta ordem.
 *
 * É um subconjunto da paleta de `utils/colors.ts` — só as que o Vuetify define com a escala
 * inteira de tons, porque a faixa do Kanban e os chips pintam com tons derivados. `black`
 * ficou de fora por não ter `lighten-*`, e as telas ficavam sem fundo a partir da quinta
 * situação.
 *
 * A ORDEM é própria e não a de `ASSIGNMENT_ORDER`: é ela que decide a cor de todo fluxo que
 * não escolheu cor nenhuma, e trocá-la repintaria os fluxos existentes. O que é compartilhado é
 * a REGRA de dar a próxima livre e de recomeçar quando esgotam, no `nextColor`.
 *
 * O cinza saiu daqui: era a cor de esgotamento, e passou a querer dizer "sem valor" no quadro.
 * Esgotadas as cores, a ordem recomeça — repetir uma cor é melhor que dizer "vazio" sobre uma
 * situação que existe.
 */
const colors = ['amber', 'blue', 'deep-purple', 'teal', 'pink', 'indigo', 'brown']

/**
 * A cor de cada situação de um fluxo: a ESCOLHIDA quando há uma, a derivada da ordem quando
 * não.
 *
 * A escolhida entra primeiro, e por isso nenhuma automática repete uma cor fixada — que é o
 * que faz as duas conviverem. Esgotadas as cores, sobra o cinza; é o preço de uma paleta
 * fechada, e quem quiser distinguir mais situações escolhe a cor.
 *
 * Resolve por id, e não por posição, para que a mesma instância do colorizador sirva a quem
 * tem o id de uma situação em mão — o card de um relacionamento, por exemplo. Durante a
 * edição de um fluxo as situações novas ainda não têm id: quem chama passa a posição no lugar
 * dele, que é chave estável do mesmo jeito.
 */
export default class StateColorizer {
    private readonly states: State[]
    /** Por id da situação; preenchido inteiro na construção, nunca depois. */
    private readonly assigned: { [stateId: number]: string } = {}

    constructor(states: State[]) {
        this.states = states
        //Primeiro as fixadas: é o que tira aquelas cores das disponíveis para as demais
        states.forEach(state => {
            if (state.color) this.assigned[state.id] = state.color
        })
        states.forEach(state => this.resolve(state))
    }

    /**
     * A cor de uma situação pelo id.
     *
     * Id que não está no fluxo cai na cor da inicial: um item pode guardar uma situação que
     * saiu do fluxo, e é melhor desenhá-lo com a cor do começo do que sem cor nenhuma. Fluxo sem
     * inicial não existe — o servidor recusa —, e por isso o último recurso é a cor que a
     * inicial teria, e não um cinza que hoje quer dizer "sem valor".
     */
    color(stateId: number | undefined): string {
        if (stateId !== undefined && this.assigned[stateId]) return this.assigned[stateId]
        const initial = this.states.find(state => state.initial)
        return (initial && this.assigned[initial.id]) || initialColor
    }

    private resolve(state: State) {
        if (this.assigned[state.id]) return
        if (state.initial && !this.isUsed(initialColor)) return this.use(state, initialColor)
        if (state.final && !this.isUsed(finalColor)) return this.use(state, finalColor)
        //Pelo nextColor, que é a mesma regra das opções de um domínio: a próxima livre, e a
        //volta ao começo quando esgotam. Registra sempre — sem isso a cor de esgotamento era
        //recalculada a cada leitura.
        this.use(state, nextColor(Object.values(this.assigned), colors))
    }

    private use(state: State, color: string) {
        this.assigned[state.id] = color
    }

    private isUsed(color: string): boolean {
        return Object.values(this.assigned).includes(color)
    }
}
